/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { NavigateBefore } from "@mui/icons-material";
import { Box, Container, Divider, Grid, Link, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import React, { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import NotFound from "./NotFound";
import Loader from "../Components/Loader/Loader";
import Constants from "../constants";
import { useCloud } from "../Hooks/Cloud";
import { useShowRegion } from "../Hooks/ShowRegion";
import { formatDate, formatTime } from "../Utils/Utility";

function Incident() {
  const { cloud } = useCloud();
  const { id } = useParams();
  const { setIsVisible: setRegionPickerVisible } = useShowRegion();

  const [ incident, setIncident ] = useState({});
  const [ isLoading, setIsLoading ] = useState(true);
  const [ intervalId, setIntervalId ] = useState(null);

  // Get refresh interval from configuration (default to 5 minutes if not set)
  const refreshInterval = window.ENV.incidentRefreshInterval || 300000;

  // Fetch incident details
  const fetchIncident = (incidentId, isRefresh = false) => {
    fetch(`${window.ENV.server.endpoint}/api/v1/incidents/${incidentId}?cloud=${cloud}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          setIncident(undefined);
          setIsLoading(false);
        }

        throw new Error(response.status);
      })
      .then((data) => {
        setIncident(data);
        if (!isRefresh) {
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!isRefresh) {
          setIsLoading(false);
        }
      });
  };

  // Setup interval for refreshing incident data
  const setupRefreshInterval = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }

    const newIntervalId = setInterval(() => {
      fetchIncident(id, true); // Pass true to indicate this is a refresh
    }, refreshInterval);

    setIntervalId(newIntervalId);
  };

  useEffect(() => {
    setRegionPickerVisible(false);

    document.title = window.ENV[cloud].title + " - Incident";
    document.querySelector("meta[name=\"description\"]").setAttribute("content",
      "Gain detailed insights into past incidents and their resolutions on our Incident Page.");

    fetchIncident(id);

    // Setup refresh interval for live updates
    setupRefreshInterval();

    // Cleanup interval on component unmount or when dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [ cloud, id ]);

  // Cleanup interval when component unmounts
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Helper function to process comment content
  const processCommentContent = (comment) => {
    // Remove [code] tags and clean HTML
    let cleanedComment = comment
      .replace(/\[code\]|\[\/code\]/g, "")
      .replace(/&nbsp;/gi, " ");

    // Remove font-family and font-size from style attributes
    cleanedComment = cleanedComment.replace(/font-family\s*:\s*[^;]+;?/gi, "");
    cleanedComment = cleanedComment.replace(/font-size\s*:\s*[^;]+;?/gi, "");

    // Clean up empty style attributes and extra semicolons
    cleanedComment = cleanedComment.replace(/style\s*=\s*["'][;]*["']/gi, "");
    cleanedComment = cleanedComment.replace(/style\s*=\s*["'][\s;]*["']/gi, "");


    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(cleanedComment, {
      ALLOWED_ATTR: [ "style", "title", "href", "target", "rel" ],
      ALLOWED_TAGS: [ "p", "strong", "em", "u", "ol", "ul", "li", "br", "span", "a" ]
    });
  };

  if (isLoading) {
    return <Loader />;
  } else {
    document.title = window.ENV[cloud].title + " - " + incident.short_description;
  }

  if (!incident) {
    return <NotFound isHeaderVisible={ false } isCurrentStatusVisible={ true } />;
  }

  return (
    <Container maxWidth="md">
      <Container>
        <Box component={ "div" } sx={ { mb: 4 } } textAlign={ "center" }>
          <Link href={ "/history" } variant="body2" underline="none">
            <Typography component={ "span" } variant="inherit">
              <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                <NavigateBefore sx={ { pr: 1 } } />
                <Box component="span">
                  Incident History
                </Box>
              </Box>
            </Typography>
          </Link>
          <Typography
            variant="h4"
            component={ "div" }
            sx={ { my: 2 } }
            fontWeight={ "bold" }
            color={ Constants.color[incident.type] }
          >
            { incident.short_description }
          </Typography>
          <Typography
            variant="body1"
            component={ "span" }
            fontWeight={ "bold" }
            color={ Constants.color[incident.type] }>
            { incident.type }
          </Typography>
          <Typography variant="body1" component={ "span" } color={ "text.secondary" }>
            &nbsp;|&nbsp;Status:&nbsp;
            <Box component={ "span" } color={ Constants.color[incident.status] }>{ incident.status }</Box>
          </Typography>
        </Box>

        <Grid container rowSpacing={ 2 } spacing={ 2 } sx={ { px: 4 } }>
          { incident.comments &&
            incident.comments.map((comment, index) => {
              return (
                <Fragment key={ index }>
                  <Grid item sm={ 4 } md={ 4 }>
                    <Typography variant="body2" color={ "text.secondary" }>
                      { formatDate(comment.created_on) } <br />
                      { formatTime(comment.created_on) }
                    </Typography>
                  </Grid>
                  <Grid item sm={ 8 } md={ 8 }>
                    { /* <Typography variant="body2" fontWeight={ "bold" }>
                      { index === 0 && incident.status === "Resolved"
                        ? "Resolved"
                        : index === incident.comments.length - 1
                          ? "Investigating"
                          : "Update" }
                    </Typography> */ }
                    { /* <Typography variant="body2" textAlign={ "left" }>
                      { comment.comment.replace(/\[code\]|\[\/code\]/g, "")
                        .replace(/(<([^>]+)>)/gi, "")
                        .replace(/&nbsp;/gi, " ") }
                    </Typography> */ }
                    <Typography
                      variant="body2"
                      sx={ { textAlign: "left" } }
                      dangerouslySetInnerHTML={ {
                        __html: processCommentContent(comment.comment)
                      } }
                    />
                  </Grid>

                  { /* Add divider between updates, but not after the last one */ }
                  { index < incident.comments.length - 1 && (
                    <Grid item xs={ 12 }>
                      <Divider sx={ { my: 2, opacity: 0.3 } } />
                    </Grid>
                  ) }
                </Fragment>
              );
            }) }
        </Grid>
      </Container>
      <Container sx={ { my: 4 } }>
        <Grid container sx={ { alignItems: "center", display: "flex" } } >
          <Grid item rowSpacing={ 2 } xs={ 12 } md sm lg>
            <Box
              sx={ {
                alignItems: "center",
                display: "flex",
                justifyContent: "flex-start"
              } }
            >
              <Link href={ "/" } variant="body2" underline="none">
                <Typography component={ "span" } variant="inherit">
                  <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                    <NavigateBefore sx={ { pr: 1 } } />
                    <Box component="span">
                      Current Status
                    </Box>
                  </Box>
                </Typography>
              </Link>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
}

export default Incident;
